import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'
import { OEP_CONFIG_KEYS } from '../types'
import type { OepToggleState } from '../types'

interface CustomerConfigurationValue {
  readonly key: string
  readonly value: unknown
}

/**
 * Reads the three OEP toggle values from `customer_configuration_value`
 * (via GET /customers/:id/configurations). Each key's `value` is a JSON boolean;
 * an absent key defaults to `false`.
 */
function buildToggleState(
  values: readonly CustomerConfigurationValue[]
): OepToggleState {
  const byKey = new Map<string, unknown>(values.map((v) => [v.key, v.value]))
  return {
    showOpenEnrollmentTab:
      byKey.get(OEP_CONFIG_KEYS.showOpenEnrollmentTab) === true,
    showPlansSubTab: byKey.get(OEP_CONFIG_KEYS.showPlansSubTab) === true,
    showPeopleSubTab: byKey.get(OEP_CONFIG_KEYS.showPeopleSubTab) === true,
  }
}

export function useOepConfigurations(customerId: string) {
  return useQuery({
    queryKey: ['oepConfigurations', customerId],
    queryFn: async (): Promise<OepToggleState> => {
      const res = await epsClient.get<CustomerConfigurationValue[]>(
        `/customers/${customerId}/configurations`
      )
      return buildToggleState(res.data ?? [])
    },
    enabled: Boolean(customerId),
    // The toggle UI holds an optimistic cache; a background focus refetch must
    // not clobber a live/just-saved edit (app QueryClient defaults to refetch).
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}
